import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import {
    Drawer,
    DrawerSection,
    DrawerDetailRow,
    DrawerFooter,
} from '@/components/ui/drawer'
import { getAccountById } from '@/api/accounts.api'
import type { Account } from '@/types/api'

interface AccountDetailDrawerProps {
    accountId: string | null
    onClose: () => void
    onUpdate?: (account: Account) => void
    showUnitColumn?: boolean
}

export function AccountDetailDrawer({
  accountId,
  onClose,
  onUpdate,
  showUnitColumn,
}: AccountDetailDrawerProps) {
  const { data: account, isLoading } = useQuery({
    queryKey: ['account-detail', accountId],
    queryFn: ({ signal }) =>
      accountId ? getAccountById(accountId, signal) : Promise.resolve(null),
    enabled: !!accountId,
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  return (
    <Drawer
      open={!!accountId}
      onClose={onClose}
      title="XEM CHI TIẾT THÔNG TIN TÀI KHOẢN"
      styles={{ body: { overflow: 'auto' } }}
      footer={
        <DrawerFooter>
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            className="min-w-[160px] h-10"
          >
            Đóng
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => account && onUpdate?.(account)}
            className="min-w-[160px] h-10"
            disabled={!account || isLoading}
          >
            Cập nhật
          </Button>
        </DrawerFooter>
      }
    >
      {isLoading && (
        <div className="p-6 text-center text-gray-500">
          Đang tải chi tiết...
        </div>
      )}

      {account && (
        <div>
          <DrawerSection>
            <DrawerDetailRow label="Họ và tên:" value={account.fullName} />
            <DrawerDetailRow label="Số điện thoại:" value={account.phone} />
            <DrawerDetailRow label="Email:" value={account.email} />
            {showUnitColumn && (
              <DrawerDetailRow label="Đơn vị:" value={account.unitName || '-'} />
            )}
            <DrawerDetailRow
              label="Nhóm quyền:"
              value={account.groupPermissionName || '-'}
            />
            <DrawerDetailRow label="Ghi chú:" value={account.note ?? '-'} alignTop/>
            <DrawerDetailRow
              label="Trạng thái:"
              value={
                <span
                  className={` px-4 py-1.5 rounded-full text-xs font-semibold ${
                    account.status
                      ? 'bg-[#EAF7ED] text-[#2FB14F]'
                      : 'bg-[#FDECEB] text-[#EE4036]'
                  }`}
                >
                  {account.status === 1 ? 'Hoạt động' : 'Không hoạt động'}
                </span>
              }
            />
          </DrawerSection>
        </div>
      )}
    </Drawer>
  )
}